import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Award, Globe } from 'lucide-react';
import { companyInfo, sectors } from '../mock/data';

const About = () => {
  return (
    <div className="dark-theme pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="display-large text-white mb-6">
              {companyInfo.tagline}
            </h1>
            <p className="body-large text-gray-300 mb-8 max-w-3xl mx-auto">
              {companyInfo.description} We combine deep business experience with technical expertise to deliver measurable results through consulting, analytics, and automation.
            </p>
            <div className="text-bluemp-400 heading-2 italic">
              "Enabling people, optimizing processes, delivering trustworthy outcomes"
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="display-medium text-white mb-6">Our Vision</h2>
              <p className="body-large text-gray-300 mb-8">
                {companyInfo.vision}
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Target className="text-bluemp-400" size={24} />
                  <span className="body-medium text-white">Practical AI Implementation</span>
                </div>
                <div className="flex items-center gap-4">
                  <Award className="text-bluemp-400" size={24} />
                  <span className="body-medium text-white">Ethical Standards</span>
                </div>
                <div className="flex items-center gap-4">
                  <Users className="text-bluemp-400" size={24} />
                  <span className="body-medium text-white">Human-Centered Approach</span>
                </div>
                <div className="flex items-center gap-4">
                  <Globe className="text-bluemp-400" size={24} />
                  <span className="body-medium text-white">Accessible Technology</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
              <h3 className="heading-2 text-bluemp-400 mb-6">What Sets Us Apart</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">South African expertise with global AI capabilities</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">Deep industry knowledge in financial services and insurance</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">Commitment to ethical AI practices and transparency</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">Proven track record of measurable business outcomes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Four Pillars */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Built on Four Core Pillars
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Every solution we deliver is grounded in these fundamental principles that ensure sustainable success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {companyInfo.pillars.map((pillar, index) => (
              <div key={index} className="group dark-hover p-8 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-bluemp-400/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center">
                    <span className="heading-3 text-bluemp-400">{index + 1}</span>
                  </div>
                  <h3 className="heading-2 text-white">{pillar}</h3>
                </div>
                <p className="body-medium text-gray-300">
                  {pillar === 'Trust' && 'We build transparent, accountable AI systems that organizations and their customers can rely on with confidence.'}
                  {pillar === 'Innovation' && 'We leverage cutting-edge AI technologies to solve real business challenges with creative, practical solutions.'}
                  {pillar === 'Integrity' && 'Every AI solution we develop adheres to the highest ethical standards, respecting privacy and promoting fairness.'}
                  {pillar === 'Reliability' && 'Our solutions deliver consistent, measurable results with ongoing support to ensure long-term success.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors We Serve */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Sectors We Serve
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Our expertise spans critical industries where data accuracy, compliance, and ethical AI practices are paramount.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {sectors.map((sector, index) => (
              <div key={index} className="text-center p-6 rounded-lg bg-gray-900/50 backdrop-blur-sm border border-bluemp-400/20 dark-hover">
                <h3 className="heading-3 text-white mb-2">{sector}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Partner with Us for Responsible AI Solutions
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Join organizations across South Africa and beyond who trust Blue MP to deliver AI solutions that combine innovation with integrity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Start Your AI Journey
              <ArrowRight size={20} />
            </Link>
            <Link to="/solutions" className="btn-secondary">
              Explore Our Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;